/**
 * ================================================================
 * CUSTOM HOOK: useNotifications
 * ================================================================
 *
 * @hook             useNotifications
 * @description      Hook for managing notification state and operations
 * @category         Business Logic Hook
 *
 * @parameters
 * - uid: string - User identifier for fetching notifications
 * - initialTab?: NotificationStatus - Initial active tab (default: 'All')
 *
 * @returns
 * - activeTab: NotificationStatus - Current active tab
 * - notifications: Notification[] - Current notifications list
 * - isLoading: boolean - Loading state for API calls
 * - error: any - Error state from API calls
 * - hasMore: boolean - Whether more pages are available
 * - unreadCount: number - Total unread notifications count
 * - loadMore: Function - Load next page of notifications
 * - changeTab: Function - Change active tab and reset data
 * - markAsRead: Function - Mark notification as read
 * - markAllAsRead: Function - Mark all notifications as read
 *
 * @sideEffects
 * - Network requests: Fetches notifications when tab changes
 * - State updates: Manages loading, error, and pagination states
 * - Cache invalidation: Updates cache when marking notifications as read
 *
 * @dependencies
 * - React hooks: useState, useCallback, useMemo
 * - RTK Query: useGetUserNotificationsQuery, useMarkNotificationAsReadMutation
 *
 * @usage
 * ```tsx
 * const {
 *   activeTab,
 *   notifications,
 *   isLoading,
 *   unreadCount,
 *   loadMore,
 *   changeTab,
 *   markAsRead,
 *   markAllAsRead
 * } = useNotifications('VJ', 'All');
 * ```
 * ================================================================
 */

import { useCallback, useMemo, useState } from 'react';

import {
  useGetUserNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
} from '@/services/notificationsService';
import type { Notification, NotificationStatus } from '@/types/notifications';

interface UseNotificationsOptions {
  /** Initial active tab */
  initialTab?: NotificationStatus;
  /** Number of notifications per page */
  pageSize?: number;
  /** Enable or disable API calls */
  enabled?: boolean;
}

export function useNotifications(uid: string, options: UseNotificationsOptions = {}) {
  const { initialTab = 'All', pageSize = 10, enabled = true } = options;

  // ================================================================
  // STATE MANAGEMENT
  // ================================================================
  //
  // Local state for pagination and tab management
  // Tracks current page, active tab, and accumulated notifications
  // ================================================================

  const [activeTab, setActiveTab] = useState<NotificationStatus>(initialTab);
  const [currentPage, setCurrentPage] = useState(1);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);

  // ================================================================
  // API QUERIES
  // ================================================================
  //
  // Main query: Fetch notifications for current tab and page
  // Mutation: Mark notifications as read
  // ================================================================

  const {
    data: apiResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetUserNotificationsQuery({
    uid,
    page: currentPage,
    limit: pageSize,
    status: activeTab,
  }, {
    skip: !enabled || !uid, // Skip API call if disabled or no uid
  });

  const [markAsRead, { isLoading: isMarkingAsRead }] = useMarkNotificationAsReadMutation();
  const [markAllAsRead, { isLoading: isMarkingAllAsRead }] =
    useMarkAllNotificationsAsReadMutation();

  // ================================================================
  // DERIVED STATE
  // ================================================================
  //
  // Compute values from API response and local state
  // Handle pagination and unread count calculation
  // ================================================================

  const notifications = useMemo(() => {
    if (currentPage === 1) {
      return apiResponse?.data.notifications || [];
    }

    // For subsequent pages, append to existing notifications
    if (apiResponse?.data.notifications) {
      const newNotifications = apiResponse.data.notifications;
      const existingIds = new Set(allNotifications.map((n) => n.id));
      const uniqueNewNotifications = newNotifications.filter((n) => !existingIds.has(n.id));

      return [...allNotifications, ...uniqueNewNotifications];
    }

    return allNotifications;
  }, [apiResponse, allNotifications, currentPage]);

  const pagination = apiResponse?.data.pagination;
  const hasMore = pagination ? currentPage < pagination.pages : false;
  const unreadCount = pagination?.unread_count || 0;

  // ================================================================
  // ACTION HANDLERS
  // ================================================================
  //
  // Functions for user interactions and state changes
  // Tab switching, pagination, and marking notifications as read
  // ================================================================

  /**
   * Load more notifications (next page)
   *
   * @returns {Promise<void>} Promise that resolves when more data is loaded
   */
  const loadMore = useCallback(async () => {
    if (hasMore && !isFetching) {
      // ================================================================
      // PAGINATION LOGIC
      // ================================================================
      //
      // Step 1: Update current page to trigger new API call
      // Step 2: New data will be appended via notifications memoization
      // Step 3: Update local state for infinite scroll behavior
      // ================================================================

      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);

      // Store current notifications before page change
      if (currentPage === 1 && apiResponse?.data.notifications) {
        setAllNotifications(apiResponse.data.notifications);
      }
    }
  }, [hasMore, isFetching, currentPage, apiResponse]);

  /**
   * Change active tab and reset pagination
   *
   * @param {NotificationStatus} newTab - New tab to activate
   * @returns {void}
   */
  const changeTab = useCallback(
    (newTab: NotificationStatus) => {
      if (newTab !== activeTab) {
        // ================================================================
        // TAB CHANGE LOGIC
        // ================================================================
        //
        // Purpose: Reset state when switching between tabs
        // Actions: Clear accumulated data, reset page, change active tab
        // Effect: Triggers new API call with updated parameters
        // ================================================================

        setActiveTab(newTab);
        setCurrentPage(1);
        setAllNotifications([]);
      }
    },
    [activeTab],
  );

  /**
   * Mark notification as read and update local state
   *
   * @param {string} notificationId - ID of notification to mark as read
   * @returns {Promise<void>} Promise that resolves when operation completes
   */
  const handleMarkAsRead = useCallback(
    async (notificationId: string) => {
      try {
        // ================================================================
        // MARK AS READ OPERATION
        // ================================================================
        //
        // Step 1: Call API to mark notification as read
        // Step 2: API mutation will invalidate cache automatically
        // Step 3: UI will re-render with updated data
        // ================================================================

        await markAsRead(notificationId).unwrap();

        // Optionally refetch to ensure data consistency
        if (activeTab === 'unread' || activeTab === 'All') {
          refetch();
        }
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    },
    [markAsRead, activeTab, refetch],
  );

  // ================================================================
  // MARK ALL AS READ HANDLER
  // ================================================================
  //
  // Purpose: Mark all notifications as read for the user
  // Effect: Updates all notifications status to READ
  // Cache: Invalidates cache to trigger UI updates
  // ================================================================

  const handleMarkAllAsRead = useCallback(async () => {
    if (!uid) return;

    try {
      await markAllAsRead(uid).unwrap();

      // Refresh current data
      refetch();

      console.log('✅ All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [markAllAsRead, uid, refetch]);

  return {
    // State
    activeTab,
    notifications,
    isLoading: isLoading && currentPage === 1, // Only show loading for initial load
    isFetching,
    isMarkingAsRead,
    isMarkingAllAsRead,
    error,

    // Pagination
    hasMore,
    currentPage,

    // Computed values
    unreadCount,

    // Actions
    loadMore,
    changeTab,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    refetch,
  };
}

/**
 * ================================================================
 * END OF HOOK: useNotifications
 * ================================================================
 *
 * @exports          useNotifications - Custom hook for notification management
 * @dependencies     React hooks, RTK Query hooks, notification types
 * @usedBy           Notification components requiring data and state management
 *
 * @performance
 * - Memoization: useMemo for notifications array and derived state
 * - Callbacks: useCallback for stable function references
 * - Pagination: Efficient infinite scroll with deduplication
 *
 * ================================================================
 */
